import * as angular from 'angular';
import * as _ from 'lodash';
import { Store, Unsubscribe } from 'redux';

import { attempt, isError } from 'lodash';
import { BugsnagHelpers } from '../lib/bugsnag-helpers';

function attemptOrLog(fn, ...args) {
  const result = attempt(fn, ...args);
  if (isError(result)) {
    console.error(result);
  }
}
/**
 * Helps angular components subscribe to the redux store. In the future this can also help
 * abstract away any store connection optimizers such as redux-watch.
 */
export class StoreConnector<T> {
  /**
   * Creates a new StoreConnector for a Store of type T
   * @class
   * @param store the redux store.
   */
  constructor(private store: Store<T>) {}

  /**
   * Subscribes an angular component to store updates on the root object.
   * @param scope the angular component scope
   * @param handler the callback, in which the next state is the root state object
   */
  connect(scope: angular.IScope, handler: (next: T) => void) {
    const unsub = this.store.subscribe(() => {
      scope.$evalAsync(() => handler(this.store.getState()));
    });
    scope.$on('$destroy', unsub);
    handler(this.store.getState());
  }

  /**
   * Subscribes an angular component to store updates on the given path in the store.
   * @param scope the angular component scope
   * @param path the path in the store to return
   * @param handler the callback with the next state passed in, or undefined if the path does not exist
   */
  connectPath<T>(scope: angular.IScope, path: string, handler: (next: T) => void) {
    const unsub = this.store.subscribe(() => {
      scope.$evalAsync(() => handler(<T>_.get(this.store.getState(), path)));
    });
    scope.$on('$destroy', unsub);
    handler(<T>_.get(this.store.getState(), path));
  }

  /**
   * Passthrough to the redux subscribe function
   * @param handler
   * @returns {Unsubscribe}
   */
  subscribe(handler: () => void): Unsubscribe {
    return this.store.subscribe(handler);
  }

  /**
   * Passthrough to the redux getState function
   * @returns {T}
   */
  getState() {
    return this.store.getState();
  }

  /**
   * Passthrough to the redux dispatch function
   * @param action
   * @returns {any}
   */
  dispatch(action: any) {
    if (action && action.type) {
      attemptOrLog(BugsnagHelpers.leaveBreadcrumbSafely, 'DISPATCH:REDUX', { type: action.type });
    }
    return this.store.dispatch(action);
  }
}
